import { Button, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import useGetVehicleTypes from '../../apiHooks/useGetVehicleTypesApi';
import { showError, showToast } from '../../constants/toasts';
import MainContainer from '../../layout/MainContainer';
import {
  useAddDriveTypeMutation,
  useLazyGetDriveTypeByIdQuery,
  useUpdateDriveTypeMutation,
} from '../../service/driveType';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';

const AddDriveType = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [vehicleType, setVehicleType] = useState<any>('default');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data, load } = useGetVehicleTypes();
  const [addDriveType] = useAddDriveTypeMutation();
  const [updateDriveType] = useUpdateDriveTypeMutation();
  const [getDetail] = useLazyGetDriveTypeByIdQuery();

  const handleVehicleType = (event: SelectChangeEvent) => {
    setVehicleType(event.target.value as any);
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: '',
    },

    validationSchema: Yup.object({
      name: Yup.string()
        .required('This field is required')
        .min(2, 'Minimum 2 characters are required'),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      if (vehicleType === 'default') {
        return;
      }
      const body = {
        name: values?.name || '',
        ...(id ? {} : { vehicleId: vehicleType }),
      };

      if (id) {
        try {
          setLoading(true);
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateDriveType({
            body: encryptedBody,
            id,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Drive type updated successfully');
            navigate('/vehicle-specifications/drive-type');
          }
        } catch (error: any) {
          setLoading(true);

          showError(error?.data?.message || '');
          console.error(error);
        }
      } else {
        try {
          setLoading(true);

          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addDriveType({
            body: encryptedBody,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Drive type added successfully');
            navigate('/vehicle-specifications/drive-type');
          }
        } catch (error: any) {
          setLoading(true);
          showError(error?.data?.message || '');
          console.error(error);
        }
      }
    },
  });

  const getData = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        formik.setFieldValue('name', response?.data?.name);
        setVehicleType(response?.data?.vehicle?._id);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getData(id);
    }
  }, []);

  return (
    <MainContainer>
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Drive Type</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/vehicle-specifications/drive-type')}>
              Vehicle Specifications
            </span>{' '}
            -{' '}
            <span onClick={() => navigate('/vehicle-specifications/drive-type')}>Drive Types</span>{' '}
            - {id ? 'Edit' : 'Add'} Drive Type
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Select Vehicle Type</label>
              <Select
                fullWidth
                labelId="vehicleType"
                id="vehicleType"
                value={vehicleType}
                disabled={id ? true : false}
                onChange={handleVehicleType}
              >
                <MenuItem disabled value="default">
                  Select Vehicle Type
                </MenuItem>
                {data?.length
                  ? data?.map((item, i: number) => {
                      return (
                        <MenuItem value={item?._id} key={i}>
                          {item?.name || '-'}
                        </MenuItem>
                      );
                    })
                  : null}
              </Select>
              {error && vehicleType === 'default' ? (
                <h6 className="err_msg">Vehicle type is required</h6>
              ) : null}
            </div>
            <div className="control_group w_50">
              <label>Drive Type</label>
              <TextField
                variant="outlined"
                placeholder="Drive Type"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                className="text_field"
                inputProps={{ maxLength: 30 }}
                value={formik.values.name}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button
              className="btn btn_secondary"
              // onClick={() => navigate("/vehicle-specifications/drive-type")}
              type="submit"
              onClick={() => setError(true)}
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddDriveType;
